import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgProgress } from '@ngx-progressbar/core';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';
import { ToastrService } from 'ngx-toastr';
import { Observable, forkJoin } from 'rxjs';
import { APIOperableComponent } from '../../../../shared/components/api-operable/api-operable.component';
import { ConfirmationComponent } from '../../../../shared/components/confirmation/confirmation.component';
import { ConfirmationModel } from '../../../../shared/model/confirmation.model';
import { UIDialogService } from '../../../../shared/services/ui-dialog.service';
import { DataTableUtils } from '../../../../shared/utils/data-table.utils';
import { MiscellaneousUtils } from '../../../../shared/utils/miscellaneous.utils';
import { SortUtils } from '../../../../shared/utils/sort.utils';
import { AuthService } from '../../../security/service/auth.service';

import {
  CustomerModel,
  GroupModel,
  PersonaModel,
  ServiceProviderModel,
  UserModel
} from '../../model/user.model';
import { UsersService } from '../../service/users.service';

@Component({
  selector: 'user-modal',
  templateUrl:
    './console/app-js/modules/user/components/user-modal/user-modal.component.html'
})
export class UserModalComponent extends APIOperableComponent implements OnInit {
  @Input('opened') opened = false;
  @Input('title') title = 'Edit User';
  @Input('user') user: any;
  @Output() closed = new EventEmitter<any>();
  @ViewChild('templateForm', { static: true }) templateForm: NgForm;
  @ViewChildren('kendoMultiSelectInstance')
  protected kendoMultiSelectInstances: Array<MultiSelectComponent>;
  protected originalData: Array<any> = [];
  protected temporaryData: Array<any> = [];
  protected groupList: Array<any> = [];
  protected groupsSelected: Array<any> = [];
  protected tempGroupsSelected: Array<any> = [];
  protected personaSelected: Array<any> = [];
  protected tmpPersonaSelected: Array<any> = [];
  public isValidEmail = true;
  public emailTooLong = false;
  protected selectedSites: any;
  protected sitesList: Array<any> = [];
  protected originalSitesData: Array<any> = [];
  protected sitesLoadingIndicator: boolean;
  protected selectedCustomers: any;
  protected customersList: Array<any> = [];
  protected originalCustomersData: Array<any> = [];
  protected customersLoadingIndicator: boolean;
  protected personasList: Array<PersonaModel>;
  protected groupsOnly: Array<GroupModel>;
  protected serviceProvidersList: Array<any> = [];
  protected SPLoadingIndicator: boolean;
  protected originalSPsData: Array<any> = [];
  protected selectedProviders: any;
  protected initialGroup: any;
  protected personaName: any;
  public isEnterpriseSP: boolean;
  private hasChanges: boolean;
  private SERVICEPROVIDERGROUP = 'Service Provider';
  private SERVICEPROVIDERMOBILEGROUP = 'Service Provider Mobile';
  private CUSTOMERGROUP = 'Customer';
  protected firstNameDisabled: boolean;
  protected lastNameDisabled: boolean;
  protected emailDisabled: boolean = false;
  protected groupsDisabled: boolean;
  protected personaDisabled: boolean;
  protected userOriginalState: string;

  public statusSwitch: boolean;
  public statusDeactivated: boolean;
  public statusActive: string;

  constructor(
    progressService: NgProgress,
    toastr: ToastrService,
    authService: AuthService,
    private dialogService: UIDialogService,
    private usersService: UsersService
  ) {
    super(progressService, toastr, authService);
  }

  ngOnInit() {
    this.personaSelected = [];
    if (this.authService.isEnterpriseSPUserManager()) {
      this.isEnterpriseSP = true;
      this.personaSelected.push({ persona: 'Service Provider' });
    } else {
      this.isEnterpriseSP = false;
    }
    this.user = JSON.parse(JSON.stringify(this.user));
    this.loadingIndicator = true;

    if (this.user.status && this.user.status === 'Active') {
      this.statusSwitch = true;
      this.statusActive = this.user.status;
      this.userOriginalState = this.user.status;
      this.enableEditFields();
    } else {
      this.statusSwitch = false;
      this.statusActive = this.user.status;
      this.userOriginalState = this.user.status;
      this.disableEditFields();
    }

    this.usersService.getUserInfo(this.user).subscribe((res: any) => {
      this.loadingIndicator = false;
      this.user.groups = res.groups;
      this.groupsSelected = this.user.groups;

      this.loadUserData();
    });

    if (this.title === 'Edit User') {
      this.emailDisabled = true;
    }
  }

  protected onStatusClicked(): void {
    if (this.statusSwitch === true) {
      this.statusActive = 'Active';
      this.user.status = 'Active';
      this.enableEditFields();
      if (this.userOriginalState == this.statusActive) this.hasChanges = false;
      else this.hasChanges = true;
    } else {
      this.statusActive = 'Inactive';
      this.user.status = 'Deactivated';
      this.disableEditFields();
      if (this.userOriginalState == this.statusActive) this.hasChanges = false;
      else this.hasChanges = true;
    }
  }

  private enableEditFields() {
    this.firstNameDisabled = false;
    this.lastNameDisabled = false;
    this.personaDisabled = false;
    this.groupsDisabled = false;
  }

  private disableEditFields() {
    this.firstNameDisabled = true;
    this.lastNameDisabled = true;
    this.personaDisabled = true;
    this.groupsDisabled = true;
  }

  public close(isActionPerformed: boolean): void {
    this.closed.emit({ isActionPerformed: isActionPerformed });
    this.opened = false;
  }

  /**
   * This hacky code will always set the scroll to top index element when the dropdown closes.
   * @param $event
   */
  setFocusToTopIndex($event, index): void {
    if ($event) {
      $event.preventDefault();
    }
    this.kendoMultiSelectInstances.forEach((element, i) => {
      if (i === index) {
        element['selectionService'].focusedIndex = 0;
        element.toggle(false);
      }
    });
  }

  /**
   * Load all user groups.
   */
  loadUserData(): void {
    this.loadingIndicator = true;
    // Groups data
    this.usersService.getGroups().subscribe(
      result => {
        this.originalData = SortUtils.sortCollection(
          result.groups,
          'name'
        ).slice();
        this.groupList = [...this.originalData].filter(
          group => group.type === 'group'
        );
        // Persona Loader
        this.assemblePersonaGroupData(result);
        this.loadingIndicator = false;
      },
      () => null
    );
    if (!this.isEnterpriseSP) {
      this.loadCustomers();
    }
    this.loadServiceProviders();
  }
  isPersonaSelected(persona: string) {
    return (
      this.personaSelected.length > 0 &&
      this.personaSelected.some(p => p.persona === persona)
    );
  }

  onValueChangePersonaList($event) {
    this.hasChanges = true;
    this.groupList = [];
    this.groupsOnly = [];
    if (this.title === 'Add User') {
      // initialize metadata
      this.user.metadata.customer = new CustomerModel();
      this.user.metadata.serviceProvider = new ServiceProviderModel();
    } else {
      if (!this.user.metadata.serviceProvider) {
        this.user.metadata.serviceProvider = new ServiceProviderModel();
      }

      if (!this.user.metadata.customer) {
        this.user.metadata.customer = new CustomerModel();
      }
      //bind original data when changed to its original persona
      if (
        $event.every(p =>
          this.tmpPersonaSelected.map(tp => p.persona === tp.persona)
        )
      ) {
        this.groupsSelected = this.tempGroupsSelected;
        if (
          this.selectedProviders &&
          this.selectedProviders.serviceProviderNumbers &&
          this.selectedProviders.serviceProviderNumbers.length > 0
        ) {
          this.user.metadata.serviceProvider = this.selectedProviders;
        }
        if (this.selectedCustomers) {
          this.user.metadata.customer = this.selectedCustomers;
        }
      } else {
        this.groupsSelected = [];
        this.user.metadata.serviceProvider = new ServiceProviderModel();
        this.user.metadata.customer = new CustomerModel();
      }
      // }
    }

    this.groupsOnly = this.originalData.filter(specificGrp =>
      $event.some(
        p => p.persona === specificGrp.persona && specificGrp.type === 'group'
      )
    );

    this.groupList = this.groupsOnly;
    // bind selected groups if persona selected or removed
    this.groupsSelected = this.groupsSelected.filter(gs =>
      this.groupList.some(g => gs.name === g.name)
    );
    if (this.groupsSelected.length > 0) {
      const allGroups = this.tempGroupsSelected.filter(
        ts => !this.groupList.some(g => ts.name === g.name)
      );

      // Combine the filtered groups with the all the groups
      this.groupsSelected = [...this.groupsSelected, ...allGroups];
    }

    // reset form element: groups to pristine state
    if (this.templateForm.controls && this.templateForm.controls.groups) {
      this.templateForm.controls.groups.markAsPristine();
      this.templateForm.controls.groups.markAsUntouched();
    }
  }

  /**
   *
   * Assemble Persona Data into required format
   */
  assemblePersonaGroupData(resultSet) {
    // get Personas
    this.personasList = resultSet.groups.filter(
      personaElement =>
        personaElement.type === 'persona' &&
        !(
          personaElement.name === this.SERVICEPROVIDERGROUP ||
          personaElement.name === this.SERVICEPROVIDERMOBILEGROUP ||
          personaElement.name === this.CUSTOMERGROUP
        )
    );
    // groupsOnly
    this.groupsOnly = resultSet.groups.filter(
      groupsElement => groupsElement.type === 'group'
    );
    if (this.title === 'Edit User') {
      if (!this.isEnterpriseSP) {
        // get persona of the user
        this.initialGroup = [];
        this.initialGroup = this.personasList.filter(p =>
          this.user.groups.find(g => p.persona === g.name)
        );
      } else {
        this.initialGroup = this.personasList;
      }
      // bind user persona to the persona field
      if (this.initialGroup.length > 0) {
        this.personaSelected = JSON.parse(JSON.stringify(this.initialGroup));
        // bind group list associated with persona
        this.groupList = this.groupsOnly.filter(specificGrp =>
          this.personaSelected.some(p => specificGrp.persona === p.name)
        );
        // bind selected groups by removing persona
        this.groupsSelected = this.groupsSelected.filter(
          item => this.personaSelected.findIndex(p => item.name === p.name) < 0
        );
        this.tmpPersonaSelected = this.personaSelected;
        this.tempGroupsSelected = this.groupsSelected;
      }

      if (this.user.metadata.serviceProvider) {
        this.selectedProviders = this.user.metadata.serviceProvider;
      } else {
        this.user.metadata.serviceProvider = new ServiceProviderModel();
      }

      if (this.user.metadata.customer) {
        this.selectedCustomers = this.user.metadata.customer;
      } else {
        this.user.metadata.customer = new CustomerModel();
      }
    }
    if (this.isEnterpriseSP && this.title === 'Add User') {
      this.initialGroup = this.personasList[0];
    }
  }

  /**
   * Load ServiceProviders data
   */
  loadServiceProviders() {
    this.loadingIndicator = true;
    this.SPLoadingIndicator = true;
    this.usersService.getServiceProviders().subscribe(
      result => {
        this.originalSPsData = SortUtils.sortCollection(
          MiscellaneousUtils.safeAccess(result, 'results'),
          'number'
        ).slice();
        this.serviceProvidersList = [...this.originalSPsData];
      },
      () => null,
      () => {
        if (
          MiscellaneousUtils.safeAccess(
            this.user,
            'metadata.serviceProvider.serviceProviderNumbers'
          )
        ) {
          this.user.metadata.serviceProvider.serviceProviderNumbers = this.user.metadata.serviceProvider.serviceProviderNumbers
            .map(oldSPNumber => {
              let SPnum = oldSPNumber.number ? oldSPNumber.number : oldSPNumber;
              return this.originalSPsData.find(spNumber => {
                return spNumber.number === SPnum;
              });
            })
            .filter(SPElement => {
              return SPElement !== undefined;
            });
          this.SPLoadingIndicator = false;
        } else {
          this.SPLoadingIndicator = false;
        }
        this.loadingIndicator = false;
      }
    );
  }

  /**
   * Load All Customers
   */
  loadCustomers(): void {
    this.customersLoadingIndicator = true;
    this.usersService.getCustomers().subscribe(
      result => {
        this.originalCustomersData = SortUtils.sortCollection(
          MiscellaneousUtils.safeAccess(result, 'results'),
          'number'
        ).slice();
        this.customersList = [...this.originalCustomersData];
      },
      () => null,
      () => {
        this.customersLoadingIndicator = false;
        if (this.customersList) {
          if (
            MiscellaneousUtils.safeAccess(
              this.user,
              'metadata.customer.customerNumbers'
            )
          ) {
            this.user.metadata.customer.customerNumbers = this.user.metadata.customer.customerNumbers.map(
              (cNum: any) => {
                let customer = this.customersList.find(x => x.number === cNum);
                if (customer) {
                  return customer;
                } else {
                  return { number: cNum };
                }
              }
            );
            this.loadSites();
          }
        }
      }
    );
  }

  /**
   * Load sites for the selected customerNumbers
   */
  loadSites(): void {
    this.sitesLoadingIndicator = true;
    this.usersService
      .getAllSitesForCustomers(
        this.user.metadata.customer.customerNumbers
          .map((customer: any) => customer.number)
          .join('||')
      )
      .subscribe(
        result => {
          this.originalSitesData = SortUtils.sortCollection(
            MiscellaneousUtils.safeAccess(result, 'results'),
            'id'
          ).slice();
          this.sitesList = [...this.originalSitesData];
        },
        () => null,
        () => {
          this.sitesLoadingIndicator = false;
          if (this.user.metadata.customer.siteIds) {
            this.user.metadata.customer.siteIds = this.user.metadata.customer.siteIds
              .map(oldSite => {
                let id = oldSite.id !== undefined ? oldSite.id : oldSite;
                return this.originalSitesData.find(site => {
                  return site.id === id;
                });
              })
              .filter(site => {
                return site !== undefined;
              });
          }
        }
      );
  }

  /**
   * Update selected sites if they are not in the updated sites results after selecting/removing customers
   */
  updateSites(): void {
    if (
      this.user.metadata.customer.customerNumbers &&
      this.user.metadata.customer.customerNumbers.length !== 0
    ) {
      this.loadSites();
    } else {
      this.user.metadata.customer.siteIds = [];
    }
  }

  /**
   * Filter group list.
   * @param $event
   */
  onFilterGroupList($event) {
    this.groupList = DataTableUtils.filterDataByProperty(
      this.groupList,
      this.groupsOnly,
      $event,
      'name'
    );
  }

  /**
   * Filter sites list.
   * @param $event
   */
  onFilterSitesList($event) {
    this.sitesList = DataTableUtils.filterDataByProperty(
      this.sitesList,
      this.originalSitesData,
      $event,
      'number'
    );
  }

  /**
   * Filter ServiceProviders list.
   * @param $event
   */
  onFilterServiceProvidersList($event) {
    this.serviceProvidersList = DataTableUtils.filterDataByProperty(
      this.serviceProvidersList,
      this.originalSPsData,
      $event,
      'number'
    );
  }

  /**
   * Filter Customers list.
   * @param $event
   */
  onFilterCustomersList($event) {
    this.customersList = DataTableUtils.filterDataByProperty(
      this.customersList,
      this.originalCustomersData,
      $event,
      'number'
    );
  }

  /**
   * Update hasChanges Boolean
   */
  changesMade(): boolean {
    return (
      this.templateForm.controls.email.dirty ||
      this.templateForm.controls.firstName.dirty ||
      this.templateForm.controls.lastName.dirty ||
      this.hasChanges
    );
  }

  updateChanges() {
    this.hasChanges = true;
  }

  /**
   * On Save button click.
   */
  onSave(): void {
    // place selected persona as groups
    if (this.groupsSelected != undefined) {
      var tmpSelectedGroups = [...this.groupsSelected, ...this.personaSelected];
    } else {
      var tmpSelectedGroups = this.personaSelected;
    }
    if (this.title === 'Edit User') {
      let newGroups = tmpSelectedGroups.filter(
        r => this.user.groups.findIndex(l => r.id === l.id) < 0
      );
      let removedGroups = this.user.groups.filter(
        r => tmpSelectedGroups.findIndex(l => r.id === l.id) < 0
      );
      this.editUser(newGroups, removedGroups, this.user);
    } else {
      this.createUser(tmpSelectedGroups, this.user);
    }
  }

  /**
   * On Password Reset button click.
   */
  onPasswordReset(): void {
    if (this.title === 'Edit User') {
      if (
        this.authService.isUsersManagerAdmin() ||
        this.authService.isEnterpriseSPUserManager()
      ) {
        this.resetPassword(this.user.id, this.user.email);
      }
    }
  }

  private setupMetadata(): void {
    if (
      MiscellaneousUtils.safeAccess(
        this.user,
        'metadata.customer.customerNumbers'
      )
    ) {
      this.user.metadata.customer.customerNumbers = this.user.metadata.customer.customerNumbers.map(
        customer =>
          customer && typeof customer === 'object' ? customer.number : customer
      );
    }
    if (MiscellaneousUtils.safeAccess(this.user, 'metadata.customer.siteIds')) {
      this.user.metadata.customer.siteIds = this.user.metadata.customer.siteIds.map(
        site => (site && typeof site === 'object' ? site.id : site)
      );
    }

    if (
      MiscellaneousUtils.safeAccess(
        this.user,
        'metadata.serviceProvider.serviceProviderNumbers'
      )
    ) {
      this.user.metadata.serviceProvider.serviceProviderNumbers = this.user.metadata.serviceProvider.serviceProviderNumbers.map(
        sp => (sp && typeof sp === 'object' ? sp.number : sp)
      );
    }

    if (
      MiscellaneousUtils.safeAccess(
        this.user,
        'metadata.serviceProvider.serviceProviderNumbers'
      )
    ) {
      let assignedSPs = [];
      this.user.metadata.serviceProvider.serviceProviderNumbers.forEach(
        elNumber => {
          const tempSP = this.originalSPsData.find(elem => {
            return elem.number === elNumber;
          });
          if (tempSP) {
            delete tempSP.links;
            delete tempSP.isActive;
            delete tempSP.type;
            tempSP.id = '0';
            assignedSPs.push(tempSP);
          }
        }
      );
      this.user.metadata.serviceProvider.serviceProviders = assignedSPs;
    }
  }

  /**
   * Perform the add and remove operations if detected on user selections.
   * @param newGroups
   * @param user
   */
  private createUser(newGroups: Array<any>, user: UserModel): void {
    this.startProgress();
    this.setupMetadata();
    this.submitAddUser(user, newGroups, false);
  }

  private submitAddUser(
    user: UserModel,
    newGroups: Array<any>,
    enableExistingRegisteredUser: boolean
  ): void {
    this.usersService.addUser(user, enableExistingRegisteredUser).subscribe(
      result => {
        user.id = result.id;
      },
      (errorResponse: any) => {
        const errorBody =
          errorResponse && errorResponse.error
            ? errorResponse.error
            : errorResponse;
        if (!enableExistingRegisteredUser && errorBody && errorBody.duplicate) {
          this.promptEnableExistingUser(user, newGroups);
          return;
        }
        this.onAPIError(
          errorBody && errorBody.message
            ? errorBody.message
            : 'An error occurred while creating the user.'
        );
      },
      () => {
        this.usersService
          .addGroups(newGroups.map(item => item.id), user.id)
          .subscribe(
            () => {
              this.onAPISuccess('User Created successfully');
              this.close(true);
            },
            () => {
              this.onAPIError('An error occurred while adding User Groups');
            }
          );
      }
    );
  }

  private promptEnableExistingUser(
    user: UserModel,
    newGroups: Array<any>
  ): void {
    this.finishProgress();
    this.dialogService
      .open(ConfirmationComponent, [
        {
          provide: ConfirmationModel,
          useValue: new ConfirmationModel(
            'Existing User',
            'User already registered. Enable this user?'
          )
        }
      ])
      .then(confirmed => {
        if (confirmed) {
          this.startProgress();
          this.submitAddUser(user, newGroups, true);
        } else {
          this.toastr.info('No changes were made to the existing user.');
          this.close(false);
        }
      });
  }

  /**
   * Perform the add and remove operations if detected on user selections.
   * @param newGroups
   * @param removedGroups
   * @param user
   */
  private editUser(
    newGroups: Array<any>,
    removedGroups: Array<any>,
    user: UserModel
  ): void {
    this.setupMetadata();
    this.modifyGroupsUpdateProfile(newGroups, removedGroups, user);
  }

  private addGroupsUpdateProfile(newGroups: Array<any>, user: UserModel): void {
    this.loadingIndicator = true;
    if (newGroups.length > 0) {
      this.usersService
        .addGroups(newGroups.map(item => item.id), user.id)
        .subscribe((resultsAdd: any) => {
          if (resultsAdd) {
            this.onAPISuccess('User Groups have been assigned successfully');
            this.updateUserProfile(user);
          } else {
            this.onAPIError('An error occurred while adding User Groups');
          }
        });
    } else {
      this.updateUserProfile(user);
    }
  }

  private modifyGroupsUpdateProfile(
    newGroups: Array<any>,
    removedGroups: Array<any>,
    user: UserModel
  ): void {
    this.loadingIndicator = true;
    if (removedGroups.length > 0) {
      this.usersService
        .deleteGroups(removedGroups.map(item => item.id), user.id)
        .subscribe((resultsDelete: any) => {
          if (resultsDelete) {
            this.onAPISuccess('User Groups have been removed successfully');
            this.addGroupsUpdateProfile(newGroups, user);
          } else {
            this.onAPIError('An error occurred while removing User Groups');
          }
        });
    } else {
      this.addGroupsUpdateProfile(newGroups, user);
    }
  }

  private updateUserProfile(user: UserModel): void {
    let apiCalls: Array<Observable<any>> = [];
    this.loadingIndicator = true;
    let operationResults: Array<any> = [null, null, null];
    let isSuccess = false;
    apiCalls.push(this.usersService.editUserInfo(user));
    apiCalls.push(this.usersService.updateUserAccountStatus(user));

    const observables = forkJoin<any>(apiCalls);
    observables.subscribe({
      next: results => {
        if (results.length === 3) {
          operationResults = results.map((item: any) => item.success);
        } else {
          operationResults[2] = results[0];
        }
      },
      complete: () => {
        this.onAPISuccess('User has been successfully updated');
        isSuccess = true;
        this.loadingIndicator = false;
        this.finishProgress();
        this.close(isSuccess);
      }
    });
  }

  private resetPassword(id: string, email: string): void {
    this.loadingIndicator = true;
    let isSuccess = false;

    this.usersService
      .changePasswordUser(id, email)
      .subscribe((results: any) => {
        if (results) {
          this.onAPISuccess('Password reset email has been sent.');
          isSuccess = true;
        } else {
          isSuccess = false;
        }
        this.loadingIndicator = false;
        this.close(isSuccess);
      });
  }

  /**
   * On Cancel/Close button event.
   */
  onCancelClose(): void {
    if (this.changesMade()) {
      const text = this.title === 'Add User' ? 'creating' : 'editing';
      this.dialogService
        .open(ConfirmationComponent, [
          {
            provide: ConfirmationModel,
            useValue: new ConfirmationModel(
              'Confirm',
              `You are in the process of ${text} a user. Are you sure you want to exit?`
            )
          }
        ])
        .then(confirmed => {
          if (confirmed) {
            this.close(false);
          }
        });
    } else {
      this.close(false);
    }
  }

  /**
   * Validate FirstName
   */
  firstNameValidation(): boolean {
    return (
      this.templateForm.controls.firstName &&
      this.templateForm.controls.firstName.errors &&
      (this.templateForm.controls.firstName.dirty ||
        this.templateForm.controls.firstName.touched)
    );
  }

  /**
   * Validate LastName
   */
  lastNameValidation(): boolean {
    return (
      this.templateForm.controls.lastName &&
      this.templateForm.controls.lastName.errors &&
      (this.templateForm.controls.lastName.dirty ||
        this.templateForm.controls.lastName.touched)
    );
  }

  /**
   * Validate Email
   */
  emailValidation(): boolean {
    return (
      this.templateForm.controls.email &&
      this.templateForm.controls.email.errors &&
      (this.templateForm.controls.email.dirty ||
        this.templateForm.controls.email.touched)
    );
  }

  /**
   * Validate no special characters or numbers
   */
  specialCharCheck(name: string): boolean {
    if (name) {
      return !/^[A-Za-z]+$/.test(name);
    } else {
      return false;
    }
  }

  /**
   * Validate group present for Add user
   */
  groupCheck(): boolean {
    return (
      this.personaSelected &&
      this.personaSelected.some(e => e.persona === 'Admin Portal') &&
      (this.groupsSelected === undefined || this.groupsSelected.length === 0)
    );
  }

  resetButtonVisibilityCheck(): boolean {
    if (this.title === 'Edit User') {
      if (
        (this.statusActive == 'Active' &&
          this.authService.isUsersManagerAdmin()) ||
        this.authService.isEnterpriseSPUserManager()
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  /**
   * Validate persona present for User
   */
  personaCheck(): boolean {
    return this.personaSelected === undefined || !this.personaSelected.length;
  }

  /**
   * Validate The User Can Save
   */
  saveDisabled(): boolean {
    return (
      this.isLoading() ||
      !this.user.firstName ||
      !this.user.lastName ||
      !this.user.email ||
      this.groupCheck() ||
      this.specialCharCheck(this.user.firstName) ||
      this.specialCharCheck(this.user.lastName) ||
      (!this.isValidEmail &&
        this.templateForm.controls.email &&
        this.templateForm.controls.email.dirty) ||
      this.emailTooLong ||
      !this.changesMade()
    );
  }

  /**
   * Data is Loading
   */
  isLoading(): boolean {
    return (
      this.loadingIndicator ||
      this.customersLoadingIndicator ||
      this.sitesLoadingIndicator ||
      (this.SPLoadingIndicator &&
        this.personaSelected.some(e => e.persona === 'Service Provider'))
    );
  }

  /**
   * Only allow alphabetical characters
   * @param e character the user keypresses
   */
  protected alphaOnly(e) {
    const key = e.keyCode;
    return (key >= 65 && key <= 90) || (key >= 97 && key <= 122) || key === 32;
  }

  /**
   * Checks if email is a valid email
   * @param email string
   */
  private checkValidEmail(email) {
    let emailRegex = new RegExp(
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
    this.isValidEmail = emailRegex.test(email) ? true : false;
  }

  /**
   * Checks if vemail is too long for auth0 to save
   * @param email string
   */
  private checkLongEmail(email) {
    if (email.indexOf('@') > 0) {
      this.emailTooLong =
        email.split('@')[0].length > 64 || email.split('@')[1].length > 254
          ? true
          : false;
    }
  }

  /**
   * Checks if valid email, vixxo email, and long email
   * @param email string
   */
  protected emailValidations(email): void {
    this.checkLongEmail(email);
    this.checkValidEmail(email);
  }
}
